import cn from 'classnames'
import { useId } from 'react'

import { isMixed } from 'components/uiKit/KitUtils'
import { t } from 'services/Translation'
import { testPropsEl } from 'utils/test/qaData'

import { Tooltip } from '../Dropdown'
import type { IKitControlProps, IOption } from '../KitTypes'
import { KitSize } from '../KitTypes'
import * as s from './Segmented.module.scss'
import SegmentedElement from './SegmentedElement'

export const DEFAULT_OPTIONS: IOption<boolean>[] = [
  { value: false, label: t('common.no') },
  { value: true, label: t('common.yes') },
]

export const INVERTED_DEFAULT_OPTIONS: IOption<boolean>[] = [
  { value: true, label: t('common.no') },
  { value: false, label: t('common.yes') },
]
export type SegmentedValue = string | boolean | number
export interface ISegmentedProps<T extends SegmentedValue = SegmentedValue>
  extends IKitControlProps<T> {
  options?: IOption<T>[]
  fluid?: boolean
  width?: number
  resettable?: boolean
  minWidth?: number
}

const Segmented = <T extends number | string | boolean>({
  name,
  defaultValue,
  value = defaultValue,
  onChange,
  onFocus,
  onBlur,
  disabled,
  size = KitSize.M,
  fluid = false,
  options = DEFAULT_OPTIONS as IOption<T>[],
  readOnly,
  minWidth,
  resettable,
  width,
}: ISegmentedProps<T>) => {
  const safeValue = isMixed(value) ? undefined : (value as T | undefined)
  const safeDefault = isMixed(defaultValue) ? undefined : (defaultValue as T | undefined)
  const groupName = useId()

  return (
    <div
      {...testPropsEl('switcher', { name }, { disabled: !!disabled || !!readOnly })}
      className={cn(s.root, disabled && s.disabled, fluid && s.fluid)}
      onBlur={onBlur}
      onFocus={onFocus}
      style={{ width }}
    >
      {options.map((item, i) => (
        <Tooltip key={i} mouseEnterDelay={0.5} overlay={item.tooltip} placement='top'>
          <div className={s.option}>
            <SegmentedElement
              disabled={disabled || readOnly}
              groupName={groupName}
              item={item}
              key={String(item.value)}
              minWidth={minWidth}
              name={name}
              onChange={onChange}
              resettable={resettable}
              size={size}
              value={safeValue ?? safeDefault}
            />
            <div className={s.divider} />
          </div>
        </Tooltip>
      ))}
    </div>
  )
}

export default Segmented
